import { useQuery } from "@tanstack/react-query";
import { MarketTag } from "../components/MarketTag";
import { Link, useNavigate, useParams } from "react-router-dom";

import { api } from "../api";
import { SortableTable } from "../components/SortableTable";
import { FollowButton, SimBadge, Stars } from "../components/social";
import { dd, pct, timeAgo, tone } from "../format";
import { LAYOUT_NAME } from "../nav";
import type { Role } from "../api";
import { useSession } from "../session";
import { Avatar } from "../shell/Avatar";
import { Card, EmptyState } from "../shell/ui";
import { PublisherMark } from "../components/Badges";
import { directionColumn } from "../components/direction";

interface ProfileResponse {
  user: {
    id: string;
    username: string;
    displayName: string;
    role: Role;
    bio: string;
    avatarSeed: string;
    isSimulated: boolean;
    createdAt: string;
    lastSeenAt: string | null;
    publisherBadge?: "verified" | null;
  };
  strategies: Array<{
    id: string;
    name: string;
    rating_avg: number;
    rating_count: number;
    fork_count: number;
    published_at: string;
    latest_version: number;
  }>;
  bots: Array<{
    id: string;
    name: string;
    symbol: string;
    timeframe: string;
    market_type?: string;
    leverage?: number;
    direction?: "long" | "short";
    status: string;
    return_pct: number | null;
    max_drawdown_pct: number | null;
    trade_count: number;
    started_at: string | null;
  }>;
  runs: Array<{
    id: string;
    title: string | null;
    symbol: string;
    timeframe: string;
    market_type?: string;
    leverage?: number;
    total_return_pct: number;
    max_drawdown_pct: number;
    published_at: string;
    verify_ok_count: number;
  }>;
  followers: number;
  following: number;
  comments: number;
}

export function ProfilePage() {
  const { username = "" } = useParams();
  const navigate = useNavigate();
  const { user: me } = useSession();
  const q = useQuery({
    queryKey: ["profile", username],
    queryFn: () => api<ProfileResponse>(`/hub/users/${encodeURIComponent(username)}`),
    retry: false,
  });
  if (q.isLoading) return null;
  if (!q.data) {
    return (
      <Card>
        <EmptyState icon="◇" title="No such member">
          Either this profile doesn't exist or its owner keeps it private.
        </EmptyState>
      </Card>
    );
  }
  const { user: u } = q.data;
  return (
    <div className="stack">
      <section className="card">
        <div className="card__body profile">
          <Avatar seed={u.avatarSeed} size={72} title={u.displayName} />
          <div className="profile__main">
            <h1 className="page__title">
              {u.displayName} <PublisherMark badge={u.publisherBadge} />
            </h1>
            <p className="muted">
              @{u.username} · {LAYOUT_NAME[u.role]} · member since{" "}
              {new Date(u.createdAt).toLocaleDateString()} {u.lastSeenAt && `· seen ${timeAgo(u.lastSeenAt)}`}
            </p>
            {u.isSimulated && (
              <p className="small">
                <SimBadge /> A simulated community member. Its posts are generated; every number it shows
                comes from real backtests and paper bots.
              </p>
            )}
            {u.bio && <p className="profile__bio">{u.bio}</p>}
            <p className="small">
              <strong>{q.data.followers}</strong> followers · <strong>{q.data.following}</strong> following ·{" "}
              {q.data.strategies.length} strategies · {q.data.bots.length} bots · {q.data.comments} comments
            </p>
          </div>
          {/* Your own page: you can't follow yourself (the API says so with a
              400), but you can edit it. */}
          {me?.id === u.id ? (
            <Link className="btn" to="/settings">
              Edit profile
            </Link>
          ) : (
            <FollowButton type="user" id={u.id} invalidate={[["profile", username]]} />
          )}
        </div>
      </section>
      <Card title="Published strategies" flush>
        <SortableTable
          exportName={["strategies", u.username]}
          rows={q.data.strategies}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/s/${r.id}`)}
          empty={<p className="card__body muted">Nothing published yet.</p>}
          columns={[
            {
              key: "name",
              label: "Strategy",
              render: (r) => <strong>{r.name}</strong>,
              sortValue: (r) => r.name,
              firstDirection: "asc",
            },
            {
              key: "v",
              label: "Version",
              align: "right",
              render: (r) => `v${r.latest_version}`,
              sortValue: (r) => r.latest_version,
            },
            {
              key: "rating",
              label: "Rating",
              render: (r) => <Stars value={r.rating_avg} count={r.rating_count} size="sm" />,
              sortValue: (r) => (r.rating_count ? r.rating_avg : null),
            },
            {
              key: "forks",
              label: "Forks",
              align: "right",
              render: (r) => r.fork_count,
              sortValue: (r) => r.fork_count,
            },
            {
              key: "when",
              label: "Published",
              align: "right",
              render: (r) => timeAgo(r.published_at),
              sortValue: (r) => new Date(r.published_at).getTime(),
            },
          ]}
        />
      </Card>
      <Card title="Public bots" flush>
        <SortableTable
          exportName={["bots", u.username]}
          rows={q.data.bots}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/b/${r.id}`)}
          empty={<p className="card__body muted">No public bots.</p>}
          columns={[
            {
              key: "name",
              label: "Bot",
              render: (r) => <strong>{r.name}</strong>,
              sortValue: (r) => r.name,
              firstDirection: "asc",
            },
            {
              key: "market",
              label: "Market",
              render: (r) => (
                <span className="mono small">
                  {r.symbol} {r.timeframe} <MarketTag marketType={r.market_type} leverage={r.leverage} />
                </span>
              ),
              sortValue: (r) => r.symbol,
              firstDirection: "asc",
            },
            directionColumn(),
            {
              key: "ret",
              label: "Return",
              align: "right",
              render: (r) => <span className={tone(r.return_pct)}>{pct(r.return_pct, 2)}</span>,
              sortValue: (r) => r.return_pct,
            },
            {
              key: "dd",
              label: "Max DD",
              align: "right",
              render: (r) => dd(r.max_drawdown_pct),
              sortValue: (r) => r.max_drawdown_pct,
              firstDirection: "asc",
            },
            {
              key: "trades",
              label: "Trades",
              align: "right",
              render: (r) => r.trade_count,
              sortValue: (r) => r.trade_count,
            },
          ]}
        />
      </Card>
      <Card title="Benchmarks" flush>
        <SortableTable
          exportName={["benchmarks", u.username]}
          rows={q.data.runs}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/r/${r.id}`)}
          empty={<p className="card__body muted">No published benchmarks.</p>}
          columns={[
            {
              key: "title",
              label: "Benchmark",
              render: (r) => r.title ?? `${r.symbol} ${r.timeframe}`,
              sortValue: (r) => r.title ?? "",
              firstDirection: "asc",
            },
            {
              key: "ret",
              label: "Return",
              align: "right",
              render: (r) => <span className={tone(r.total_return_pct)}>{pct(r.total_return_pct)}</span>,
              sortValue: (r) => r.total_return_pct,
            },
            {
              key: "dd",
              label: "Max DD",
              align: "right",
              render: (r) => dd(r.max_drawdown_pct, 1),
              sortValue: (r) => r.max_drawdown_pct,
              firstDirection: "asc",
            },
            {
              key: "verified",
              label: "Verified",
              align: "right",
              render: (r) => r.verify_ok_count || "—",
              sortValue: (r) => r.verify_ok_count,
            },
          ]}
        />
      </Card>
    </div>
  );
}
